import {
  FoodDetailsBody,
  FoodDetailsContent,
  FoodDetailsHeader,
  FoodDetailsInfo,
  IngredientList,
  IngredientListTitle,
  StyledFoodDetails,
} from "./styled"
import { BackButton } from "components/Common/BackButton"
import Button from "components/Buttons/Button"
import { FoodListDivider } from "components/foodItem/FoodListItem/styled"
import FoodImage from "../share/FoodImage"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import { FOOD_DETAILS_BODY_ID } from "config"
import { useState } from "react"
import { useBackToHome } from "features/meal/hooks"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import useGramFromIngredients from "features/food/hooks/useGramFromIngredients"
import useMyFoodDetailsForm from "features/food/hooks/myFood/useMyFoodDetailsForm"
import MyFoodNameAndPortion from "../share/MyFoodNameAndPortion"
import useLogMealFromFood from "features/meal/hooks/logMeal/useLogMealFromFood"
import ViewOnlyIngredient from "../share/ViewOnlyIngredient"
import { MyFoodEntry } from "model/food/types"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import EditableNutrientInformation from "../share/EditableNutrientInformation"
import EditNutrientForm from "../share/EditNutrientForm"

interface IProps<T extends MyFoodEntry> {
  onBack: () => void
  food: T
  onAdd?: (food: T) => void
  mode: "add" | "log"
}

const MyFoodDetails = <T extends MyFoodEntry>(props: IProps<T>) => {
  const { onBack, food, onAdd, mode } = props

  const [displaySection, setDisplaySection] = useState<
    "FOOD_FORM" | "EDIT_NUTRITION_FORM"
  >("FOOD_FORM")

  const backToHome = useBackToHome()
  const { handleLogMealFromFood, logMealLoading } =
    useLogMealFromFood(backToHome)
  const { myFood, onPortionChange, onUnitChange, updateFoodAsManualNutrition } =
    useMyFoodDetailsForm(food)

  const gram = useGramFromIngredients(myFood)

  const [isPortionValid, setIsPortionValid] = useState(true)

  const onPortionChangeHandler = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  if (!myFood) {
    return null
  }

  if (displaySection === "EDIT_NUTRITION_FORM") {
    return (
      <EditNutrientForm
        onClose={() => {
          setDisplaySection("FOOD_FORM")
        }}
        meal={myFood}
        onSubmit={updateFoodAsManualNutrition}
      />
    )
  }

  return (
    <StyledFoodDetails>
      <FoodDetailsContent>
        <FoodDetailsBody id={FOOD_DETAILS_BODY_ID}>
          <FoodImage previewImageUrl={myFood.previewImageUrl}>
            <TopNavWrapper>
              <FoodDetailsHeader>
                <BackButton
                  $disabled={logMealLoading}
                  onClick={() => {
                    if (!logMealLoading) {
                      onBack()
                    }
                  }}
                >
                  <ChevronLeftBtn />
                </BackButton>
              </FoodDetailsHeader>
            </TopNavWrapper>
          </FoodImage>
          <FoodDetailsInfo>
            <MyFoodNameAndPortion
              food={myFood}
              unitValue={myFood.unit}
              portionValue={myFood.portion}
              onPortionChange={onPortionChangeHandler}
              onUnitChange={onUnitChange}
              setIsPortionValid={setIsPortionValid}
              calories={myFood.calorie}
              gram={gram}
            />

            <EditableNutrientInformation
              entry={myFood}
              setEditMode={() => {
                setDisplaySection("EDIT_NUTRITION_FORM")
              }}
            />

            {myFood.ingredients.length ? (
              <IngredientList>
                <IngredientListTitle>Ingredients</IngredientListTitle>{" "}
                {myFood.ingredients.map((ingredient, index) => (
                  <div key={index}>
                    <ViewOnlyIngredient ingredient={ingredient} isManualNutrition={myFood.isManualNutrition} />
                    {index !== (food.ingredients?.length ?? 1) - 1 && (
                      <FoodListDivider />
                    )}
                  </div>
                ))}
              </IngredientList>
            ) : undefined}
          </FoodDetailsInfo>
        </FoodDetailsBody>

        <AppStyleCtaBox>
          {mode === "log" ? (
            <Button
              onClick={() => handleLogMealFromFood(myFood)}
              style={{ width: "100%" }}
              $type="primary"
              disabled={!isPortionValid}
              loading={logMealLoading}
            >
              Log this item
            </Button>
          ) : (
            <Button
              onClick={() => {
                onAdd?.(myFood)
                onBack()
              }}
              style={{ width: "100%" }}
              $type="primary"
              disabled={!isPortionValid}
              loading={logMealLoading}
            >
              Add this item
            </Button>
          )}
        </AppStyleCtaBox>
      </FoodDetailsContent>
    </StyledFoodDetails>
  )
}

export default MyFoodDetails
